// File: src/context/AIContext.js
// Description: AI Context provider for managing AI feature states and functionality

import React, { createContext, useState, useContext, useEffect } from 'react';
import { AuthContext } from './AuthContext';

// Create the AI Context
export const AIContext = createContext();

// AI Provider wraps the app and provides AI state and functions
export const AIProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  
  // AI Personalization State
  const [personalizationEnabled, setPersonalizationEnabled] = useState(false);
  const [personalizationRules, setPersonalizationRules] = useState([]);
  const [personalizationAnalytics, setPersonalizationAnalytics] = useState({
    totalViews: 0,
    personalizedViews: 0,
    topViewers: [],
    contentAdaptations: []
  });

  // AI Assistant State
  const [chatHistory, setChatHistory] = useState([]);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [aiInsights, setAiInsights] = useState([]);
  const [networkingTips, setNetworkingTips] = useState([]);

  // Career Insights State
  const [careerInsights, setCareerInsights] = useState({
    trajectory: [],
    skillGaps: [],
    industryTrends: [],
    nextSteps: [],
    marketOpportunities: [],
    networkingStrategy: {},
    learningPath: {},
    careerGoalsAssessment: {}
  });
  const [careerGoals, setCareerGoals] = useState({
    shortTerm: '',
    mediumTerm: '',
    longTerm: '',
    targetIndustries: [],
    targetRoles: []
  });

  // Load AI preferences from localStorage on mount
  useEffect(() => {
    const loadAIPreferences = () => {
      try {
        const storedPersonalization = localStorage.getItem('aiPersonalization');
        const storedCareerGoals = localStorage.getItem('aiCareerGoals');
        
        if (storedPersonalization) {
          const parsed = JSON.parse(storedPersonalization);
          setPersonalizationEnabled(parsed.enabled || false);
          setPersonalizationRules(parsed.rules || []);
        }
        
        if (storedCareerGoals) {
          setCareerGoals(JSON.parse(storedCareerGoals));
        }
      } catch (error) {
        console.error('Error loading AI preferences:', error);
      }
    };

    if (user) {
      loadAIPreferences();
    }
  }, [user]);

  // Save AI preferences to localStorage
  const saveAIPreferences = () => {
    try {
      localStorage.setItem('aiPersonalization', JSON.stringify({
        enabled: personalizationEnabled,
        rules: personalizationRules
      }));
      
      localStorage.setItem('aiCareerGoals', JSON.stringify(careerGoals));
    } catch (error) {
      console.error('Error saving AI preferences:', error);
    }
  };

  // AI Personalization Functions
  const togglePersonalization = () => {
    setPersonalizationEnabled(prev => !prev);
    saveAIPreferences();
  };

  const addPersonalizationRule = (rule) => {
    setPersonalizationRules(prev => [...prev, { ...rule, id: Date.now() }]);
    saveAIPreferences();
  };

  const updatePersonalizationRule = (id, updates) => {
    setPersonalizationRules(prev => 
      prev.map(rule => rule.id === id ? { ...rule, ...updates } : rule)
    );
    saveAIPreferences();
  };

  const removePersonalizationRule = (id) => {
    setPersonalizationRules(prev => prev.filter(rule => rule.id !== id));
    saveAIPreferences();
  };

  // AI Assistant Functions
  const addChatMessage = (message, isAI = false) => {
    const newMessage = {
      id: Date.now(),
      text: message,
      isAI,
      timestamp: new Date().toISOString()
    };
    setChatHistory(prev => [...prev, newMessage]);
  };

  const clearChatHistory = () => {
    setChatHistory([]);
  };

  const toggleChat = () => {
    setIsChatOpen(prev => !prev);
  };

  const addAIInsight = (insight) => {
    setAiInsights(prev => [...prev, { ...insight, id: Date.now() }]);
  };

  const addNetworkingTip = (tip) => {
    setNetworkingTips(prev => [...prev, { ...tip, id: Date.now() }]);
  };

  // Career Insights Functions
  const updateCareerGoals = (goals) => {
    setCareerGoals(prev => ({ ...prev, ...goals }));
    saveAIPreferences();
  };

  const addCareerInsight = (type, insight) => {
    setCareerInsights(prev => {
      // Handle different data types appropriately
      if (type === 'trajectory') {
        return { ...prev, trajectory: [...(prev.trajectory || []), insight] };
      } else if (type === 'skillGaps') {
        return { ...prev, skillGaps: [...(prev.skillGaps || []), insight] };
      } else if (type === 'industryTrends') {
        return { ...prev, industryTrends: [...(prev.industryTrends || []), insight] };
      } else if (type === 'nextSteps') {
        return { ...prev, nextSteps: [...(prev.nextSteps || []), insight] };
      } else if (type === 'marketOpportunities') {
        return { ...prev, marketOpportunities: [...(prev.marketOpportunities || []), insight] };
      } else if (type === 'networkingStrategy') {
        return { ...prev, networkingStrategy: insight };
      } else if (type === 'learningPath') {
        return { ...prev, learningPath: insight };
      } else if (type === 'careerGoalsAssessment') {
        return { ...prev, careerGoalsAssessment: insight };
      }
      
      // Default case
      return { ...prev, [type]: [...(prev[type] || []), insight] };
    });
  };

  const updatePersonalizationAnalytics = (analytics) => {
    setPersonalizationAnalytics(prev => ({ ...prev, ...analytics }));
  };

  // Context value
  const value = {
    // AI Personalization
    personalizationEnabled,
    personalizationRules,
    personalizationAnalytics,
    togglePersonalization,
    addPersonalizationRule,
    updatePersonalizationRule,
    removePersonalizationRule,
    updatePersonalizationAnalytics,

    // AI Assistant
    chatHistory,
    isChatOpen,
    aiInsights,
    networkingTips,
    addChatMessage,
    clearChatHistory,
    toggleChat,
    addAIInsight,
    addNetworkingTip,

    // Career Insights
    careerInsights,
    careerGoals,
    updateCareerGoals,
    addCareerInsight
  };

  return (
    <AIContext.Provider value={value}>
      {children}
    </AIContext.Provider>
  );
};

// Custom hook to use AI Context
export const useAI = () => {
  const context = useContext(AIContext);
  if (!context) {
    throw new Error('useAI must be used within an AIProvider');
  }
  return context;
};
